import type { Socket } from 'socket.io-client';
import { sessionBlocked } from '@/stores/sessionTransition';
import { sessionSnapshot } from '@/lib/session-snapshot';

type Listener = (...args: never[]) => void;

/** Fence callbacks themselves, including events queued before React effect cleanup. */
export function sessionSocketListeners(socket: Pick<Socket, 'on' | 'off'>) {
  const snapshot = sessionSnapshot();
  const registered = new Map<string, Map<Listener, Listener>>();
  return {
    on(event: string, handler: Listener) {
      const wrapped: Listener = (...args) => {
        if (!sessionBlocked() && snapshot === sessionSnapshot()) handler(...args);
      };
      const handlers = registered.get(event) ?? new Map<Listener, Listener>();
      const previous = handlers.get(handler);
      if (previous) socket.off(event, previous as (...args: unknown[]) => void);
      handlers.set(handler, wrapped);
      registered.set(event, handlers);
      socket.on(event, wrapped as (...args: unknown[]) => void);
    },
    off(event: string, handler: Listener) {
      const wrapped = registered.get(event)?.get(handler);
      if (wrapped) socket.off(event, wrapped as (...args: unknown[]) => void);
      registered.get(event)?.delete(handler);
    },
  };
}
